/**
 * Add Contact From Signature Panel
 *
 * Toggleable panel that isolates the email signature into an editable
 * textarea, then hands the box content to the CRM create-contact drawer.
 */

import { t } from '../../../i18n.js';
import { SignatureExtractor } from '../../../services/extractors/signature-extractor.js';
import { crmNavigation } from '../../../services/crm-navigation.js';

export class AddContactFromSignaturePanel {
  constructor(wizard) {
    this.wizard = wizard;
    this.isOpen = false;
    this.isLoading = false;
    this.root = null;
    this.panel = null;
    this.textarea = null;
    this.statusEl = null;
    this.copyBtn = null;
    this.createBtn = null;
    this.toggleBtn = null;
  }

  render() {
    this.root = document.createElement('div');
    this.root.className = 'signature-panel-root';

    this.toggleBtn = document.createElement('button');
    this.toggleBtn.type = 'button';
    this.toggleBtn.className = 'control-chip signature-extract-btn';
    this.toggleBtn.textContent = t('add_contact_from_signature_button');
    this.toggleBtn.addEventListener('click', () => this.toggle());
    this.root.appendChild(this.toggleBtn);

    this.panel = document.createElement('div');
    this.panel.className = 'signature-panel';
    this.panel.hidden = true;

    this.statusEl = document.createElement('p');
    this.statusEl.className = 'signature-panel-status';
    this.panel.appendChild(this.statusEl);

    this.textarea = document.createElement('textarea');
    this.textarea.className = 'signature-panel-textarea';
    this.textarea.rows = 6;
    this.textarea.spellcheck = false;
    this.textarea.addEventListener('input', () => this._onTextChange());
    this.panel.appendChild(this.textarea);

    const actions = document.createElement('div');
    actions.className = 'signature-panel-actions';

    this.createBtn = document.createElement('button');
    this.createBtn.type = 'button';
    this.createBtn.className = 'wizard-btn wizard-btn-primary signature-create-btn';
    this.createBtn.textContent = t('signature_create_contact_button');
    this.createBtn.addEventListener('click', () => this._openCreateContact());
    actions.appendChild(this.createBtn);

    const secondary = document.createElement('div');
    secondary.className = 'signature-panel-actions-secondary';

    const pasteBtn = document.createElement('button');
    pasteBtn.type = 'button';
    pasteBtn.className = 'control-chip';
    pasteBtn.textContent = t('signature_paste_button');
    pasteBtn.addEventListener('click', () => this._pasteFromClipboard());
    secondary.appendChild(pasteBtn);

    this.copyBtn = document.createElement('button');
    this.copyBtn.type = 'button';
    this.copyBtn.className = 'control-chip signature-copy-btn';
    this.copyBtn.textContent = t('signature_copy_button');
    this.copyBtn.addEventListener('click', () => this._copyToClipboard());
    secondary.appendChild(this.copyBtn);

    actions.appendChild(secondary);
    this.panel.appendChild(actions);
    this.root.appendChild(this.panel);

    this._syncActions();
    return this.root;
  }

  async toggle() {
    if (this.isOpen) {
      this._close();
      return;
    }
    await this.open();
  }

  async open() {
    this.isOpen = true;
    this.panel.hidden = false;
    this.toggleBtn.classList.add('active');

    const cached = this.wizard.state.getSignatureText();
    if (cached !== null) {
      this._applyText(cached, { focus: true });
      return;
    }

    this.isLoading = true;
    this.textarea.disabled = true;
    this.textarea.value = '';
    this.textarea.placeholder = t('signature_loading');
    this.statusEl.textContent = t('signature_loading');
    this._syncActions();

    try {
      const { text } = await SignatureExtractor.extract(this.wizard.item);
      this.wizard.state.setSignatureText(text);
      this._applyText(text, { focus: true });
    } catch (error) {
      console.error('Signature panel extract failed:', error);
      this.wizard.state.setSignatureText('');
      this._applyText('', { focus: true });
    } finally {
      this.isLoading = false;
      this.textarea.disabled = false;
      this.textarea.placeholder = '';
      this._syncActions();
    }
  }

  _close() {
    this.isOpen = false;
    this.panel.hidden = true;
    this.toggleBtn.classList.remove('active');
  }

  _applyText(text, { focus = false } = {}) {
    this.textarea.value = text || '';
    this.statusEl.textContent = (text || '').trim()
      ? t('signature_detected_status')
      : t('signature_fallback_status');
    this._syncActions();
    if (focus) {
      this.textarea.focus();
    }
  }

  _onTextChange() {
    const value = this.textarea.value;
    this.wizard.state.setSignatureText(value);
    this.statusEl.textContent = value.trim()
      ? t('signature_detected_status')
      : t('signature_fallback_status');
    this._syncActions();
  }

  _hasSignatureText() {
    return Boolean((this.textarea?.value || '').trim());
  }

  _syncActions() {
    if (this.copyBtn) {
      this.copyBtn.disabled = !this._hasSignatureText() || this.isLoading;
    }
    if (this.createBtn) {
      this.createBtn.disabled = this.isLoading || !this._hasSignatureText();
    }
  }

  _openCreateContact() {
    const text = (this.textarea?.value || '').trim();
    if (!text) {
      this.statusEl.textContent = t('signature_create_contact_hint');
      return;
    }
    if (!crmNavigation.openCreateContactFromSignature(text)) {
      this.statusEl.textContent = t('signature_create_contact_no_instance');
      return;
    }
    this.wizard.watchForNewContacts();
  }

  async _copyToClipboard() {
    const text = this.textarea.value;
    if (!text.trim()) return;
    await this.wizard.copyToClipboard(text, t('signature_copied'));
  }

  async _pasteFromClipboard() {
    try {
      const text = await navigator.clipboard.readText();
      this.textarea.value = text;
      this.wizard.state.setSignatureText(text);
      this.statusEl.textContent = text.trim()
        ? t('signature_detected_status')
        : t('signature_fallback_status');
      this._syncActions();
      this.textarea.focus();
    } catch (error) {
      console.error('Clipboard paste failed:', error);
      this.statusEl.textContent = t('signature_paste_failed');
    }
  }
}
